import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons';
import { workflowService, dashboardService, tagService, WorkflowFilters } from '../services';
import { Workflow, Dashboard, WidgetType, Tag } from '../types';
import { useThrottle } from '../hooks';
import ImportModal from '../components/common/ImportModal';
import TagBadge from '../components/common/TagBadge';
import Button from '../components/ui/Button';
import Card, { CardHeader, CardBody, CardFooter } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import PageTopBar from '../components/ui/PageTopBar';
import EmptyState from '../components/ui/EmptyState';
import DescriptionCells from '../components/common/ExpandableDescription';
import SearchBar from '../components/ui/SearchBar';
import Select from '../components/ui/Select';

// Icons
const UploadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
    />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const FilterIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
    />
  </svg>
);

const WorkflowIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M13 10V3L4 14h7v7l9-11h-7z"
    />
  </svg>
);

const StepsIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
    />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const EmptyIcon = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1}
      d="M13 10V3L4 14h7v7l9-11h-7z"
    />
  </svg>
);

const STATUS_VARIANTS: Record<string, 'default' | 'success' | 'warning'> = {
  draft: 'default',
  published: 'success',
  archived: 'warning',
};

const WIDGET_TYPE_OPTIONS = [
  { value: 'qa', label: 'Q&A' },
  { value: 'conviction', label: 'Conviction Score' },
  { value: 'onedrive', label: 'Excel' },
  { value: 'image_repository', label: 'Image Repository' },
  { value: 'pdf_repository', label: 'PDF Repository' },
  { value: 'notes_repository', label: 'Notes Repository' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const Workflows: React.FC = () => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDashboard, setSelectedDashboard] = useState('');
  const [selectedWidgetType, setSelectedWidgetType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedTagId, setSelectedTagId] = useState<string | undefined>(undefined);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedDescId, setExpandedDescId] = useState<string | null>(null);
  const throttledSearch = useThrottle(searchQuery);

  // Sort state
  type WfSortField = 'name' | 'status' | 'steps' | 'tags' | 'updated';
  type SortDir = 'asc' | 'desc';
  const [sortField, setSortField] = useState<WfSortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (field: WfSortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'updated' ? 'desc' : 'asc');
    }
  };

  useEffect(() => {
    const loadDashboards = async () => {
      try {
        const response = await dashboardService.getDashboards(1, 100);
        setDashboards(response.data);
      } catch (err) {
        console.error('Failed to load dashboards:', err);
      }
    };
    const loadTags = async () => {
      try {
        const data = await tagService.list();
        setTags(data);
      } catch (err) {
        console.error('Failed to load tags:', err);
      }
    };
    loadDashboards();
    loadTags();
  }, []);

  const loadWorkflows = useCallback(async () => {
    try {
      setLoading(true);
      const filters: WorkflowFilters = {};
      if (throttledSearch) filters.search = throttledSearch;
      if (selectedDashboard) filters.dashboardId = selectedDashboard;
      if (selectedWidgetType) filters.widgetType = selectedWidgetType;
      if (selectedStatus) filters.status = selectedStatus as 'draft' | 'published' | 'archived';
      if (selectedTagId) filters.tagId = selectedTagId;

      const response = await workflowService.getWorkflows(page, 12, filters);
      setWorkflows(response.data);
      setTotalPages(Math.ceil(response.total / response.pageSize));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [
    page,
    throttledSearch,
    selectedDashboard,
    selectedWidgetType,
    selectedStatus,
    selectedTagId,
  ]);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  useEffect(() => {
    setPage(1);
  }, [throttledSearch, selectedDashboard, selectedWidgetType, selectedStatus, selectedTagId]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedDashboard('');
    setSelectedWidgetType('');
    setSelectedStatus('');
    setSelectedTagId(undefined);
  };

  const hasActiveFilters =
    searchQuery || selectedDashboard || selectedWidgetType || selectedStatus || selectedTagId;

  const handleCreate = () => navigate('/workflow-builder');
  const handleEdit = (id: string) => navigate(`/workflow-builder/${id}`);
  const handleImportComplete = () => {
    setIsImportModalOpen(false);
    loadWorkflows();
  };

  const getNodeCount = (workflow: Workflow) => {
    const nodes = workflow.flowData?.nodes || [];
    return nodes.filter((n: any) => n.id !== 'start' && n.type !== 'output').length;
  };

  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filterCount =
    [selectedDashboard, selectedWidgetType, selectedStatus, selectedTagId].filter(Boolean)
      .length + (searchQuery ? 1 : 0);

  // Sort workflows
  const STATUS_ORDER: Record<string, number> = { draft: 0, published: 1, archived: 2 };
  const sortedWorkflows = useMemo(() => {
    const sorted = [...workflows];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'status':
          cmp = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
          break;
        case 'steps':
          cmp = getNodeCount(a) - getNodeCount(b);
          break;
        case 'tags':
          cmp = (a.tags?.length ?? 0) - (b.tags?.length ?? 0);
          break;
        case 'updated':
          cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [workflows, sortField, sortDir]);

  const SortHeader: React.FC<{ field: WfSortField; label: string; className?: string }> = ({ field, label, className = '' }) => (
    <th
      className={`px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer select-none hover:text-neutral-700 transition-colors ${className}`}
      onClick={() => toggleSort(field)}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        <FontAwesomeIcon
          icon={sortField === field ? (sortDir === 'asc' ? faSortUp : faSortDown) : faSort}
          className={`w-3 h-3 ${sortField === field ? 'text-primary-500' : 'text-neutral-300'}`}
        />
      </span>
    </th>
  );

  return (
    <div className="h-full flex flex-col bg-neutral-50">
      <PageTopBar
        title="Workflow Library"
        description="Build multi-step workflows with dashboards for each step"
      />
      <div className="flex-1 overflow-y-auto">
      <div className="w-full px-6 lg:px-8 py-8">

        {/* Search and Filters */}
        <div className="mb-6 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search workflows..."
            />

            <Select
              value={selectedTagId ?? ''}
              onChange={e => setSelectedTagId(e.target.value || undefined)}
              options={tags.map(t => ({ value: t.id, label: t.name }))}
              placeholder="All tags"
              className="w-48"
            />

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`h-9 px-3 text-sm font-medium border rounded-lg flex items-center gap-2 transition-all duration-fast ${
                showFilters || hasActiveFilters
                  ? 'bg-primary-50 border-primary-200 text-primary-700'
                  : 'bg-surface border-neutral-200 text-neutral-600 hover:bg-neutral-50 hover:border-neutral-300'
              }`}
            >
              <FilterIcon />
              <span>Filters</span>
              {filterCount > 0 && (
                <span className="ml-1 bg-primary-600 text-white text-xs font-medium px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {filterCount}
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear all
              </Button>
            )}

            {/* Primary actions — pushed to the right */}
            <div className="ml-auto flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsImportModalOpen(true)}
              >
                Import
              </Button>
              {dashboards.length > 0 && (
                <Button variant="primary" size="sm" onClick={handleCreate}>
                  Create Workflow
                </Button>
              )}
            </div>
          </div>

          {showFilters && (
            <Card variant="default" className="animate-fade-in">
              <CardBody className="flex flex-wrap items-end gap-4">
                <Select
                  value={selectedDashboard}
                  onChange={e => setSelectedDashboard(e.target.value)}
                  options={dashboards.map(d => ({ value: d.id, label: d.name }))}
                  placeholder="All dashboards"
                  label="Uses Dashboard"
                  className="flex-1 min-w-[200px] max-w-xs"
                />
                <Select
                  value={selectedWidgetType}
                  onChange={e => setSelectedWidgetType(e.target.value)}
                  options={WIDGET_TYPE_OPTIONS}
                  placeholder="All widget types"
                  label="Contains Widget"
                  className="flex-1 min-w-[200px] max-w-xs"
                />
                <Select
                  value={selectedStatus}
                  onChange={e => setSelectedStatus(e.target.value)}
                  options={STATUS_OPTIONS}
                  placeholder="All statuses"
                  label="Status"
                  className="flex-1 min-w-[150px] max-w-[200px]"
                />
              </CardBody>
            </Card>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-center justify-between animate-fade-in">
            <span className="text-sm text-danger-700">{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-danger-500 hover:text-danger-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex flex-col justify-center items-center h-64 gap-3">
            <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            <span className="text-sm text-neutral-500">Loading workflows...</span>
          </div>
        ) : workflows.length === 0 ? (
          <EmptyState
            title={hasActiveFilters ? 'No matching workflows' : 'No workflows yet'}
            description={
              hasActiveFilters
                ? "Try adjusting your search or filters to find what you're looking for"
                : dashboards.length === 0
                  ? 'You need to create a dashboard first before you can build workflows'
                  : 'Get started by creating a workflow to orchestrate multi-step processes'
            }
            action={
              hasActiveFilters
                ? { label: 'Clear filters', onClick: clearFilters }
                : dashboards.length === 0
                  ? { label: 'Create Dashboard', onClick: () => (navigate('/dashboards')) }
                  : { label: 'Create Workflow', onClick: handleCreate }
            }
          />
        ) : (
          <Card variant="default">
            <div className="overflow-x-auto no-scrollbar">
              <table className="w-full">
                <thead>
                  <tr className="bg-neutral-50 border-y border-neutral-100">
                    <SortHeader field="name" label="Workflow" />
                    <SortHeader field="status" label="Status" />
                    <SortHeader field="steps" label="Steps" />
                    <SortHeader field="tags" label="Tags" className="hidden md:table-cell" />
                    <SortHeader field="updated" label="Updated" />
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden lg:table-cell">Description</th>
                    <th className="px-6 py-3 hidden lg:table-cell w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {sortedWorkflows.map(workflow => {
                    const stepCount = getNodeCount(workflow);
                    return (
                      <React.Fragment key={workflow.id}>
                      <tr
                        className="hover:bg-neutral-50 transition-colors cursor-pointer group"
                        onClick={() => handleEdit(workflow.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-neutral-900 group-hover:text-secondary-700 transition-colors">
                            {workflow.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant={STATUS_VARIANTS[workflow.status] || 'default'} size="sm">
                            {workflow.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                            <StepsIcon />
                            {stepCount}
                          </span>
                        </td>
                        <td className="px-6 py-4 hidden md:table-cell">
                          {workflow.tags && workflow.tags.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {workflow.tags.slice(0, 3).map(tag => (
                                <TagBadge key={tag.id} tag={tag} />
                              ))}
                              {workflow.tags.length > 3 && (
                                <Badge size="sm" variant="default">+{workflow.tags.length - 3}</Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-neutral-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-xs text-neutral-400">{formatRelativeDate(workflow.updatedAt)}</span>
                        </td>
                        <DescriptionCells
                          text={workflow.description}
                          expanded={expandedDescId === workflow.id}
                          onToggle={(e) => { e.stopPropagation(); setExpandedDescId(expandedDescId === workflow.id ? null : workflow.id); }}
                        />
                      </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <nav className="mt-8 flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              icon={<ChevronLeftIcon />}
            >
              Previous
            </Button>
            <div className="flex items-center gap-1 px-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`w-8 h-8 text-sm font-medium rounded-lg transition-colors ${
                    pageNum === page
                      ? 'bg-primary-600 text-white'
                      : 'text-neutral-600 hover:bg-neutral-100'
                  }`}
                >
                  {pageNum}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              icon={<ChevronRightIcon />}
              iconPosition="right"
            >
              Next
            </Button>
          </nav>
        )}
      </div>
      </div>

      <ImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportComplete={handleImportComplete}
        allowedTypes={['workflow', 'bundle']}
      />
    </div>
  );
};

export default Workflows;
